import { Box, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";
import { AttachFile, GroupAdd } from "@mui/icons-material";
import { format, parseISO, isValid } from "date-fns";

import CountWithLazyTooltipCell from "../../../table/cells/CountWithLazyTooltipCell";
import LatestLoginCell from "../../users/table/LatestLoginCell";
import NotesCell from "../../../table/cells/NotesCell";

function resolveStatusValue(statusValue) {
  if (statusValue && typeof statusValue === "object") {
    return {
      label: statusValue.label ?? "",
      tone: statusValue.tone ?? statusValue.variant ?? "",
      tooltip: statusValue.tooltip,
      interactive: Boolean(statusValue.interactive),
    };
  }

  return {
    label: statusValue ?? "",
    tone: "",
    tooltip: "",
    interactive: false,
  };
}

function getStatusStyles(statusValue) {
  const toneStyles = {
    success: {
      backgroundColor: "#dcfce7",
      color: "#166534",
      border: "1px solid #bbf7d0",
    },
    warning: {
      backgroundColor: "#ffedd5",
      color: "#9a3412",
      border: "1px solid #fed7aa",
    },
    danger: {
      backgroundColor: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    },
    muted: {
      backgroundColor: "#f3f4f6",
      color: "#374151",
      border: "1px solid #d1d5db",
    },
    default: {
      backgroundColor: "#f3f4f6",
      color: "#374151",
      border: "1px solid #d1d5db",
    },
  };

  const base = resolveStatusValue(statusValue);
  const normalizedLabel = base.label?.toLowerCase() || "";
  const normalizedTone = base.tone?.toLowerCase() || "";
  const pickTone = (key) => toneStyles[key] || toneStyles.default;
  let styles;

  if (normalizedTone) {
    styles = pickTone(normalizedTone);
  } else {
    switch (normalizedLabel) {
      case "connected":
      case "synced":
        styles = pickTone("success");
        break;
      case "disconnected":
      case "failed":
        styles = pickTone("danger");
        break;
      case "partial":
      case "sync needed":
        styles = pickTone("warning");
        break;
      case "not required":
      case "not synced":
        styles = pickTone("muted");
        break;
      case "sync issues":
        styles = pickTone("warning");
        break;
      case "outdated":
        styles = pickTone("danger");
        break;
      default:
        styles = pickTone("default");
    }
  }

  return { ...base, styles };
}

export function createManagementCellRenderer({
  onNoteClick,
  onSyncStatusClick,
  syncStatusLoadingCustomerId,
  dateFormat = "MM/dd/yyyy",
}) {
  const formatDateValue = (value) => {
    if (!value) {
      return "";
    }

    const parsedValue = value instanceof Date ? value : parseISO(String(value));
    if (!isValid(parsedValue)) {
      return value;
    }

    return format(parsedValue, dateFormat);
  };

  const cellRenderersByType = {
    status: (value, column, row) => {
      const statusData = getStatusStyles(value);
      const statusLabel = statusData.label || "—";
      const statusStyles = statusData.styles;
      const statusTooltip = statusData.tooltip;
      const statusInteractive = statusData.interactive;
      const customerForStatus = row.originalCustomer || row;
      const statusCustomerId =
        customerForStatus?.id || row.customerId || customerForStatus?.customerId;
      const isSyncStatusColumn = column.field === "syncStatus";
      const isStatusLoading =
        isSyncStatusColumn &&
        statusInteractive &&
        statusCustomerId &&
        statusCustomerId === syncStatusLoadingCustomerId;
      const canClick =
        isSyncStatusColumn &&
        statusInteractive &&
        typeof onSyncStatusClick === "function";

      const handleStatusClick = (event) => {
        if (!canClick) return;
        event.stopPropagation();
        onSyncStatusClick(customerForStatus, statusData);
      };

      const chip = (
        <Chip
          label={statusLabel}
          icon={
            isStatusLoading ? (
              <CircularProgress size={12} sx={{ color: "#4B5563" }} />
            ) : undefined
          }
          size="small"
          sx={{
            borderRadius: "16px",
            fontWeight: 500,
            fontSize: "0.75rem",
            ...statusStyles,
            cursor: canClick ? "pointer" : "default",
          }}
          onClick={canClick ? handleStatusClick : undefined}
        />
      );

      if (statusTooltip) {
        return (
          <Tooltip title={statusTooltip} arrow>
            <Box component="span">{chip}</Box>
          </Tooltip>
        );
      }

      return chip;
    },

    userType: (value) => (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          fontSize: "0.875rem",
          color: "#111827",
        }}
      >
        {value}
      </Typography>
    ),

    createdBy: (value, column, row) => {
      const isInternal = row.createdByType === "internal";
      const createdByColor = isInternal ? "#1d4ed8" : "#7c3aed";
      const createdByBgColor = isInternal ? "#dbeafe" : "#ede9fe";
      return (
        <Box
          sx={{
            display: "inline-block",
            px: 1.5,
            py: 0.5,
            borderRadius: "12px",
            backgroundColor: createdByBgColor,
            color: createdByColor,
            fontWeight: 500,
            fontSize: "0.875rem",
          }}
        >
          {value}
        </Box>
      );
    },

    accounts: (cellValue, column, row) => (
      <CountWithLazyTooltipCell
        endpoint={`/api/v2/profiles/${row.id}/linked_accounts/`}
        count={Number.isFinite(cellValue) ? cellValue : 0}
        icon={
          <AttachFile
            fontSize="small"
            sx={{
              color: "#6b7280",
              transform: "rotate(45deg)",
              transformOrigin: "center",
            }}
          />
        }
        typographySx={{ fontWeight: 500 }}
      />
    ),

    latestLogin: (cellValue, column, row) => (
      <LatestLoginCell value={cellValue} color={row.latestLoginColor} />
    ),

    notes: (cellValue, column, row) => (
      <NotesCell row={row} value={cellValue} onNoteClick={onNoteClick} />
    ),

    date: (value) => formatDateValue(value),
  };

  const renderCellContent = (value, column, row) => {
    if (column.field === "altUser") {
      const parsedAltUserCount = parseInt(value, 10);
      return (
        <CountWithLazyTooltipCell
          endpoint={`/api/v2/customers/${row.id}/alt_users/`}
          count={Number.isFinite(parsedAltUserCount) ? parsedAltUserCount : 0}
          icon={<GroupAdd fontSize="small" sx={{ color: "#374151" }} />}
        />
      );
    }



    if (column.field === "notes") {
      return cellRenderersByType.notes(value, column, row);
    }

    const renderer = cellRenderersByType[column.type];
    if (renderer) {
      return renderer(value, column, row);
    }

    if (value === "-" || !value) {
      return "";
    }

    return value;
  };

  return { renderCellContent };
}